import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { invoiceKeys, type Invoice } from "@/api/invoices";
import { segment } from "@/api/query";

/* Mirrors api/src/pbo_invoicing/emails/schemas.py. */

export type EmailDraft = {
  email_configured: boolean;
  from_address: string | null;
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  document_version: number | null;
  attachment_name: string | null;
};

export type EmailInput = {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  document_version: number | null;
};

export type InvoiceEmail = {
  id: string;
  document_version: number;
  document_sha256: string;
  from_address: string;
  to_addrs: string[];
  cc_addrs: string[];
  subject: string;
  body: string;
  message_id: string;
  status: "sending" | "sent" | "failed";
  error: string | null;
  created_by_username: string | null;
  created_at: string;
  finished_at: string | null;
  /** Set on a payment reminder: its kind and its number of that kind. */
  reminder_kind: "due_soon" | "overdue" | null;
  reminder_sequence: number | null;
};

/** "a@x.com, b@y.com; c@z.com" → three addresses (commas, semicolons or line breaks). */
export function parseAddresses(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((address) => address.trim())
    .filter(Boolean);
}

export function getEmailDraft(invoiceId: string): Promise<EmailDraft> {
  return apiJson<EmailDraft>(`/invoices/${segment(invoiceId)}/emails/draft`);
}

/** Sends the stored PDF; resolves with the updated invoice. A mail-server failure is a 502. */
export function sendInvoiceEmail(invoiceId: string, input: EmailInput): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(invoiceId)}/emails`, "POST", input);
}

export function useEmailDraft(invoiceId: string) {
  return useQuery({
    queryKey: ["invoices", "email-draft", invoiceId] as const,
    queryFn: () => getEmailDraft(invoiceId),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSendInvoiceEmail(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailInput) => sendInvoiceEmail(invoiceId, input),
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(invoiceId), invoice);
      return queryClient.invalidateQueries({ queryKey: invoiceKeys.lists });
    },
    // A failed attempt is still recorded: reload the history.
    onError: () => queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) }),
  });
}
